/* The Motherboard Module (Campaign Frame Materials, last page), laid beside the character sheet like the
   other tile pages and drawn at its measured positions: the Ikonis choices, the installed augments, the
   augment list with its built boxes, and the scrap collection with Current Quantum. */

import { AUGMENTS, IKONIS_DAMAGE_TYPES, IKONIS_RANGES, MOTHERBOARD, SCRAP } from './content/motherboard';
import { augmentSlots } from './motherboard';
import { capitalize, PageProps } from './SheetParts';
import { Banner, cutBox, Html, PageFooter, SheetSvg, TileStripes, Txt, Value } from './svg';

/** Trait circles in the sheet's two rows of three. */
const TRAIT_SPOTS: Record<string, [number, number]> = {
  agility: [195.2, 89.1], strength: [247.4, 89.1], finesse: [300.7, 89.1],
  presence: [195.2, 102.5], instinct: [247.4, 102.5], knowledge: [300.7, 102.5],
};
const RANGE_SPOTS: [number, number][] = [[195.2, 128.9], [195.2, 142.2], [195.2, 155.6], [285.9, 128.9], [285.9, 142.4]];
const RANGE_ORDER = ['Melee', 'Very Close', 'Close', 'Far', 'Very Far'];
const TYPE_X = [195.2, 247.4];

/** First-line baselines of the augments; the right column starts at Sear. */
const AUGMENT_BASE: Record<string, number> = {
  Force: 246.8, Guard: 270.2, Converge: 293.7, Amplify: 317.1, Scope: 349.2, Deny: 381.2, Target: 404.6, Split: 428.1, Fix: 460.1, Scare: 501.2,
  Sear: 246.8, Absorb: 279.3, Kick: 320.5, Block: 370.2, Zip: 411.9, Bury: 444.4, Follow: 477, Override: 509.5,
};
const RIGHT_COLUMN = AUGMENTS.findIndex((a) => a.name === 'Sear');
const COLUMNS = [AUGMENTS.slice(0, RIGHT_COLUMN), AUGMENTS.slice(RIGHT_COLUMN)];
/** Room down to the next augment in its column (or the frame), which its text shrinks to fit. */
const roomBelow = (name: string) => {
  const col = COLUMNS.find((list) => list.some((a) => a.name === name)) ?? [];
  const next = col[col.findIndex((a) => a.name === name) + 1];
  return (next ? AUGMENT_BASE[next.name] : 548) - AUGMENT_BASE[name] - 1.5;
};

const SLOT_Y = [108.4, 128.3, 148.2, 168.1, 188];
const SCRAP_COLS = [{ label: 200.8, line: [250.8, 291] }, { label: 331.3, line: [373.2, 413.4] }, { label: 449.6, line: [499.6, 539.9] }];
const SCRAP_ROWS = [[590.1, 609.3], [644.7, 663.9], [697.3, 716.5]];
const GROUP_Y = [572.9, 627.5, 680.1];
const RELIC_LINES = [747.7, 767.9].flatMap((y) => [[200.6, 291], [325.4, 415.8], [450.2, 539.9]].map(([x0, x1]) => ({ y, x0, x1 })));

function Circle({ at: [cx, cy], on, faint }: { at: [number, number]; on: boolean; faint?: boolean }) {
  return <circle className={`s-paper ${faint ? 'o-faint' : 'o-ink'}${on ? ' is-marked' : ''}`} cx={cx} cy={cy} r={3} />;
}

const scrapLine = (x0: number, x1: number, y: number) => `M${x0} ${y}L${x0 + 0.4} ${y + 3.3}H${x1 - 0.4}L${x1} ${y + 0.1}`;

export default function MotherboardPage({ c }: PageProps) {
  const ik = c.ikonis;
  const slots = augmentSlots(c);
  return (
    <SheetSvg label="Motherboard module" className="cs-extra">
      <TileStripes w={161} />
      <path className="s-band" d="M162.2 0V49.8H315L339.2 0Z" />
      <Txt x={178.9} y={25.4} size={15.2} weight={900} tone="band">MOTHERBOARD</Txt>
      <Txt x={178.1} y={38.8} size={11.1} weight={300} tone="band">MODULE</Txt>

      {/* Name and description */}
      <path className="o-rule" d="M359.7 13L361.3 20H522.7L524.3 13.2" />
      <path className="o-rule" d="M359.7 41.7L361.3 48.7H522.7L524.3 41.9" />
      <Value x={366} base={18.6} w={154} size={9}>{ik.name}</Value>
      <Value x={366} base={47.3} w={154} size={8}>{ik.description}</Value>
      <Txt x={367.6} y={30.1} size={6.1} weight={700}>Name</Txt>
      <Txt x={367.6} y={58.7} size={6.1} weight={700}>Description</Txt>

      {/* Ikonis */}
      <Txt x={193.7} y={73.9} size={10.1} weight={900}>IKONIS</Txt>
      <Txt x={191.7} y={84} size={8.1} weight={700} italic>{MOTHERBOARD.trait}</Txt>
      {Object.entries(TRAIT_SPOTS).map(([trait, at]) => (
        <g key={trait}>
          <Circle at={at} on={ik.trait === trait} />
          <Txt x={at[0] + 5.7} y={at[1] + 5.5} size={8.1}>{capitalize(trait)}</Txt>
        </g>
      ))}
      <Txt x={191.7} y={123.3} size={8.1} weight={700} italic>{MOTHERBOARD.range}</Txt>
      {RANGE_ORDER.map((range, i) => (
        <g key={range}>
          <Circle at={RANGE_SPOTS[i]} on={ik.range === range} />
          <Txt x={RANGE_SPOTS[i][0] + 5.8} y={RANGE_SPOTS[i][1] + 5.5} size={8.1}>
            {range} - {IKONIS_RANGES.find((r) => r.range === range)?.damage}
          </Txt>
        </g>
      ))}
      <Txt x={191.7} y={174.2} size={8.1} weight={700} italic>{MOTHERBOARD.damageType}</Txt>
      {IKONIS_DAMAGE_TYPES.map((type, i) => (
        <g key={type}>
          <Circle at={[TYPE_X[i], 180]} on={ik.damageType === type} />
          <Txt x={TYPE_X[i] + 5.7} y={185.5} size={8.1}>{type}</Txt>
        </g>
      ))}
      <Html x={191.7} y={191.6} w={172} h={20} size={8.1} className="cs-augment">
        <em><strong>{MOTHERBOARD.bonded.name}:</strong></em> {MOTHERBOARD.bonded.text}
      </Html>

      {/* Installed augments */}
      <path className="o-frame" d={cutBox(371.8, 74, 590.5, 210.2, 5.7)} />
      <Banner x0={406} x1={556.2} y={66.6} h={13.8} title="INSTALLED AUGMENTS" tab={[437.2, 525]} tabRaise={2.2} wings={[399.2, 563]} />
      <Txt x={397.2} y={97.5} size={8.1}>{MOTHERBOARD.slots}</Txt>
      {SLOT_Y.map((y, i) => (
        <g key={y}>
          <Circle at={[384.1, y - 3.6]} on={!!ik.installed[i]} faint={i >= slots} />
          <path className="o-faint" d={`M394.3 ${y}L396.8 ${y + 3.2}H581.5`} />
          <Value x={399} base={y + 1.8} w={180} size={8.5}>{ik.installed[i]}</Value>
        </g>
      ))}

      {/* Augments */}
      <path className="o-frame" d={cutBox(192.4, 222, 591.4, 545.6, 5.7)} />
      <Banner x0={208.4} x1={339.1} y={215.6} h={13.8} title="AUGMENTS" tab={[235.5, 311.9]} tabRaise={2.2} wings={[202.4, 345]} />
      {AUGMENTS.map((a, i) => {
        const right = i >= RIGHT_COLUMN;
        const base = AUGMENT_BASE[a.name];
        const boxX = right ? [404.8, 417.2] : [202, 214.5];
        const built = ik.built[a.name] ?? 0;
        return (
          <g key={a.name}>
            {boxX.slice(2 - a.boxes).map((x, k) => (
              <rect key={x} className={`s-paper o-frame o-thin${k < built ? ' is-marked' : ''}`} x={x} y={base - 10.6} width={10.2} height={10.1} rx={1.8} />
            ))}
            <Html x={right ? 430.8 : 228.1} y={base - 7.6} w={right ? 154 : 172} h={roomBelow(a.name)} size={8.1} className="cs-augment" fit>
              <p><strong>{a.name}:</strong> {a.text}</p>
              <p className="cs-augment__cost">{a.cost}</p>
              {a.precompile && <p className="cs-augment__cost">Precompile: Tier {a.precompile}</p>}
            </Html>
          </g>
        );
      })}

      {/* Scrap collection */}
      <path className="o-frame" d={cutBox(192.4, 547.7, 591.4, 782.6, 5.7)} />
      <Banner x0={209.4} x1={340.1} y={539.1} h={13.9} title="SCRAP COLLECTION" tab={[236.5, 313]} tabRaise={2.1} wings={[203.4, 346.1]} />
      <Txt x={496} y={560.5} size={8.1} weight={900}>Current</Txt>
      <Txt x={493.1} y={568.3} size={8.1} weight={900}>Quantum</Txt>
      <circle className="s-band" cx={555.9} cy={547.7} r={22.9} />
      <path className="s-paper" d="M562.9 530.9H548.9L539.1 540.7V554.7L548.9 564.5H562.9L572.7 554.7V540.7Z" />
      <path className="s-paper o-frame" d="M561.7 533.6H550.1L541.8 541.9V553.5L550.1 561.8H561.7L570 553.5V541.9Z" />
      <Txt x={555.9} y={552} size={12} weight={900} anchor="middle">{ik.quantum}</Txt>
      {SCRAP.map((g, gi) => (
        <g key={g.group}>
          <Txt x={201.1} y={GROUP_Y[gi]} size={8.1} weight={900}>
            {g.group.toUpperCase()} <tspan fontWeight={300}>({g.die.toUpperCase()})</tspan>
          </Txt>
          {g.items.map((s, i) => {
            const col = SCRAP_COLS[Math.floor(i / 2)];
            const y = SCRAP_ROWS[gi][i % 2];
            const [x0, x1] = col.line;
            return (
              <g key={s.name}>
                <Txt x={col.label} y={y} size={8.1} weight={600}>{s.value}. {s.name}</Txt>
                <path className="o-rule" d={scrapLine(x0, x1, y - 6.3)} />
                <Value x={x0} base={y - 4.4} w={x1 - x0} size={8.5} center>{ik.scrap[s.name] || ''}</Value>
              </g>
            );
          })}
        </g>
      ))}
      <Txt x={201.1} y={734.7} size={8.1} weight={900}>RELICS</Txt>
      {RELIC_LINES.map(({ y, x0, x1 }, i) => (
        <g key={`${x0} ${y}`}>
          <path className="o-rule" d={scrapLine(x0, x1, y)} />
          <Value x={x0 + 4} base={y + 1.6} w={x1 - x0 - 8} size={8}>{ik.relics[i]}</Value>
        </g>
      ))}
      <PageFooter x={24.2} />
    </SheetSvg>
  );
}
